import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, Variants } from "framer-motion";
import logo from "../assets/logo.svg";
import dummyMobileBg from "../assets/backdrop.png";
import closeIcon from "../assets/close.png"; // <-- Close icon PNG
import { NAVBAR } from "../data/constants";

const navContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
};

const navItemVariants: Variants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
};

interface MobileNavbarProps {
  onOpenEnquireModal: () => void;
}

export default function MobileNavbar({ onOpenEnquireModal }: MobileNavbarProps) {
  const [open, setOpen] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setShowProducts(false);
  }, [open]);

  const baseBg = scrolled ? "bg-[#07394a]" : "bg-[#0000]/40 backdrop-blur-md";

  return (
    <>
      {/* CLOSED HEADER */}
      {!open && (
        <header
          className={`fixed top-0 left-0 w-full h-[80px] z-50 flex items-center justify-between  py-3 transition-all duration-300 ${baseBg}`}
        >
          <Link to="/">
            <img src={logo} alt="SKTuff Logo" className="h-8 ml-[14px] w-auto" />
          </Link>
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
          >
            <motion.svg
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="h-6 w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </motion.svg>
          </button>
        </header>
      )}

      {/* SLIDING OVERLAY */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 overflow-hidden"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.6 }}
          >
            {/* BACKGROUND IMAGE */}
            <motion.div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `url(${dummyMobileBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                pointerEvents: "none",
              }}
              initial={{ x: 50 }}
              animate={{ x: 0 }}
              exit={{ x: 50 }}
              transition={{ duration: 0.8 }}
            />

            {/* NAVIGATION CONTENT */}
            <motion.div
              className="relative flex flex-col h-full z-10"
              initial={{ x: 100 }}
              animate={{ x: 0 }}
              exit={{ x: 100 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {/* TOP BAR */}
              <motion.div
                className="flex items-center justify-between px-4 py-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0, transition: { delay: 0.2, duration: 0.4 } }}
                exit={{ opacity: 0, x: 20, transition: { duration: 0.3 } }}
              >
                <Link to="/" onClick={() => setOpen(false)}>
                  <img src={logo} alt="SKTuff Logo" className="h-8 w-auto mt-[15px] cursor-pointer" />
                </Link>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setOpen(false)}
                  className="mt-2"
                >
                  <motion.img
                    src={closeIcon}
                    alt="Close"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="h-8 w-8 object-contain"
                  />
                </button>
              </motion.div>

              {/* NAV LINKS */}
              <motion.nav
                className="flex-grow px-6 pt-6 overflow-y-auto"
                variants={navContainerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <ul className="space-y-6 mt-[63px]">
                  {NAVBAR.navLinks.map(link =>
                    link.label === "Products" ? (
                      <motion.li key={link.label} variants={navItemVariants}>
                        <button
                          type="button"
                          onClick={() => setShowProducts(v => !v)}
                          className="text-white text-[24px] font-medium text-left"
                        >
                          {link.label}
                        </button>
                        <motion.ul
                          initial={false}
                          animate={showProducts ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden mt-[28px] pl-4 space-y-3"
                          style={{ pointerEvents: showProducts ? 'auto' : 'none' }}
                        >
                          {NAVBAR.products.map(p => (
                            <motion.li key={p.label} variants={navItemVariants}>
                              <Link
                                to={p.to}
                                onClick={() => setOpen(false)}
                                className="font-inter  text-white text-[24px] font-medium block"
                              >
                                {p.label}
                              </Link>
                            </motion.li>
                          ))}
                        </motion.ul>
                      </motion.li>
                    ) : (
                      <motion.li key={link.label} variants={navItemVariants}>
                        <Link
                          to={link.to}
                          onClick={() => setOpen(false)}
                          className={`text-white font-inter text-[24px] font-medium block ${
                            location.pathname === link.to ? "underline" : ""
                          }`}
                        >
                          {link.label}
                        </Link>
                      </motion.li>
                    )
                  )}
                </ul>

                {/* ENQUIRE BUTTON BELOW NAVIGATION */}
                <motion.div
                  className="pt-[75px]"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0, transition: { delay: 0.4, duration: 0.4 } }}
                  exit={{ opacity: 0, x: 20, transition: { duration: 0.3 } }}
                  
                >
                  {/* onClick={() => { setOpen(false); onOpenEnquireModal(); }} */}
                  <button className="mx-auto ml-0 block px-8 py-3 bg-white text-black font-semibold " onClick={() => { setOpen(false); onOpenEnquireModal(); }} >
                    {NAVBAR.enquireButton}
                  </button>
                </motion.div>
              </motion.nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
